/**
 * Inline SVG sparkline. No external dep.
 * Renders an area chart with a colored stroke, gradient fill, and a
 * terminal dot. Optionally draws a dashed target line.
 *
 * Used in KpiCard (34px tall, full width) and MetricDrawer (64px tall).
 */
import type { ReactElement } from 'react';

import type { KpiStatus } from '@/types/scorecard';

interface SparklineProps {
  /** Data series. Must have at least 2 points to render a line. */
  data: number[];
  /** RAG (or gated-out neutral) status governs the stroke / fill color. */
  status: KpiStatus;
  width?: number;
  height?: number;
  /** CSS class forwarded to the <svg> element. */
  className?: string;
  /** When set, draws a horizontal dashed line at this y-value. */
  targetLine?: number | null;
  /** aria-label for accessibility */
  ariaLabel?: string;
}

const STATUS_COLORS: Record<string, string> = {
  green: '#6BFFAB',
  amber: '#FFB000',
  red:   '#ef4444',
  insufficient_data: '#8a8a94',
  not_available: '#8a8a94',
  not_tracked: '#8a8a94',
  na:    '#6c6c78',
};

export function Sparkline({
  data,
  status,
  width = 200,
  height = 34,
  className,
  targetLine,
  ariaLabel,
}: SparklineProps): ReactElement {
  const color = STATUS_COLORS[status] ?? '#6c6c78';

  if (data.length < 2) {
    return (
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        height={height}
        className={className}
        aria-label={ariaLabel ?? 'No history'}
        role="img"
        preserveAspectRatio="none"
      >
        <text
          x={3}
          y={height / 2 + 4}
          fill="#6c6c78"
          fontFamily="monospace"
          fontSize={9}
        >
          no history
        </text>
      </svg>
    );
  }

  const mn = Math.min(...data);
  const mx = Math.max(...data);
  const range = mx - mn || 1;
  const step = width / (data.length - 1);
  const pad = 3;

  const X = (i: number) => +(i * step).toFixed(1);
  const Y = (v: number) => +(height - ((v - mn) / range) * (height - pad * 2) - pad).toFixed(1);

  let linePath = '';
  let areaPath = '';
  data.forEach((v, i) => {
    const x = X(i);
    const y = Y(v);
    linePath += `${i === 0 ? 'M' : 'L'}${x},${y} `;
    areaPath += `${i === 0 ? 'M' : 'L'}${x},${y} `;
  });
  areaPath += `L${X(data.length - 1)},${height} L0,${height} Z`;

  const gradId = `sg-${status}-${Math.random().toString(36).slice(2, 7)}`;

  let targetEl: ReactElement | null = null;
  if (targetLine != null && targetLine >= mn && targetLine <= mx) {
    const ty = Y(targetLine);
    targetEl = (
      <line
        x1={0}
        y1={ty}
        x2={width}
        y2={ty}
        stroke="#8a8a96"
        strokeWidth={0.8}
        strokeDasharray="3 3"
        opacity={0.7}
      />
    );
  }

  const lastX = X(data.length - 1);
  // data.length >= 2 is guaranteed by the early return above
  // reason: data array length was checked above to be >= 2
  const lastVal = data[data.length - 1] as number;
  const lastY = Y(lastVal);

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width="100%"
      height={height}
      className={className}
      aria-label={ariaLabel ?? 'Sparkline chart'}
      role="img"
      preserveAspectRatio="none"
    >
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={color} stopOpacity={0.22} />
          <stop offset="1" stopColor={color} stopOpacity={0} />
        </linearGradient>
      </defs>
      <path d={areaPath} fill={`url(#${gradId})`} />
      {targetEl}
      <path
        d={linePath}
        fill="none"
        stroke={color}
        strokeWidth={1.6}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <circle cx={lastX} cy={lastY} r={2.1} fill={color} />
    </svg>
  );
}
